import { FlashList } from '@shopify/flash-list';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Animated, TouchableOpacity } from 'react-native';

import { DateRange } from '@components/DateRangePicker/DateRangePickerModal';
import { DateRangePickerWebV3 } from '@components/DateRangePicker/DateRangePickerWebV3';
import Highlighter from '@components/HighlightWords';
import { ContactType } from '@components/Invite/Contact';
import { sortSelectedFirst } from '@components/MyTasks/myTasksFilterOptions';
import RadioButton from '@components/RadioButton/RadioButton';
import { Box, ShadowBox, Text } from '@components/Restyle';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import { FilterSectionHeader } from '@components/shared/FilterSectionHeader';
import Icon from '@components/shared/Icon/Icon';
import SearchInput from '@components/shared/SearchInput/SearchInput.web';
import { CheckBoxButton } from '@components/Web/CheckBoxButton';
import { PANEL_WIDTH } from '@components/Web/PopupActivityFilterPanel.web';
import {
  Project,
  ProjectSortOption,
  SearchableResult,
  TaskPriority,
  useSearchLazyQuery,
} from '@graphql/generated';
import { useContacts } from '@hooks/useContacts';
import { useDateRangeString } from '@hooks/useDateRangeString';
import { useDebouncedSearch } from '@hooks/useDebouncedSearch';
import { useListProjectsFromQuery } from '@hooks/useListProjectsFromQuery';
import useMe from '@hooks/useMe';
import { Search, SearchResultType } from '@root/types';
import theme from '@themes/theme';
import { convertUsersToContacts } from '@utils/convertUsersToContacts';
import { isProjectSearchResult } from '@utils/typeGuards';

export type PopupTaskFilterProjectProps = {
  onSelectProject?: (project: Project[]) => void;
  selectedProjects?: Project[];

  onSelectAssignee?: (assignees: ContactType[]) => void;
  selectedAssignees?: ContactType[];

  initialAssignees?: ContactType[];

  onSelectPriority?: (item: TaskPriority[]) => void;
  selectedPriorities?: TaskPriority[];

  onSelectDueDateRange?: (dr: DateRange) => void;
  selectedDueDateRange?: DateRange;

  selectedDueDateStrList?: string[];
  onSelectedDueDateStrList?: (x: string[]) => void;

  /**
   * Called when user clicks "Select Dates" in the Due Date section.
   * Parent can use this to show PopupTaskFilterProjectDateRangeV3.
   */
  onPressSelectDates?: () => void;

  /**
   * Which section should be open when this component is mounted.
   * - "projects" (default)
   * - "assignee"
   * - "duedate"
   * - "priority"
   */
  initiallyOpenSection?: 'projects' | 'assignee' | 'duedate' | 'priority';

  hideSearch?: boolean;
};

export type PopupTaskFilterProjectDateRangeProps = {
  onSelectDueDateRange?: (dr: DateRange) => void;
  selectedDueDateRange?: DateRange;
  isRangeSelector?: boolean;
};

// ================================
// Only date range selector panel
// (from previous showSelectDatesModal === true branch)
// ================================
// Fixed width of the date-range popup. Exported so screens that position this
// panel relative to a button can right-align it without measuring at runtime.
export const DATES_PANEL_WIDTH = 352;

export const PopupTaskFilterProjectDateRangeV3: React.FC<
  PopupTaskFilterProjectDateRangeProps
> = ({
  onSelectDueDateRange,
  selectedDueDateRange,
  isRangeSelector = false,
}) => {
  return (
    <ShadowBox
      flex={1}
      width={DATES_PANEL_WIDTH}
      alignItems='center'
      justifyContent='center'
      borderRadius='xs'
      borderWidth={1}
      borderColor='grey02'
      variant='base'
      backgroundColor='white'>
      <Box flex={1} style={{ width: DATES_PANEL_WIDTH }}>
        <DateRangePickerWebV3
          isRangeSelector={isRangeSelector}
          onSelect={(d1, d2) => {
            // Report selected date range back to parent
            onSelectDueDateRange?.({
              start: d1 ? dayjs(d1) : undefined,
              end: d2 ? dayjs(d2) : undefined,
            });
          }}
          start={selectedDueDateRange?.start?.toDate()}
          end={selectedDueDateRange?.end?.toDate()}
        />
      </Box>
    </ShadowBox>
  );
};

// ================================
// Main filter popup (Projects / Assignee / Due Date quick filters / Priority)
// (from previous showSelectDatesModal === false branch)
// ================================
const ESTIMATED_ITEM_SIZE = 36;

// Max height for the collapsible sections
const PROJECT_SECTION_MAX_HEIGHT = 36 * 4 + 8;
const ASSIGNEE_SECTION_MAX_HEIGHT = 36 * 4 + 8;
const DUE_DATE_SECTION_MAX_HEIGHT = 36 * 4 + 8;
const PRIORITY_SECTION_MAX_HEIGHT = 36 * 3 + 8;

export const PopupTaskFilterProjectV3: React.FC<
  PopupTaskFilterProjectProps
> = ({
  onSelectProject,
  selectedProjects = [],
  initialAssignees,
  selectedAssignees = [],
  onSelectAssignee,
  selectedPriorities: initSelectedPriorities = [],
  onSelectPriority,
  onSelectDueDateRange,
  selectedDueDateRange,
  selectedDueDateStrList = [],
  onSelectedDueDateStrList, // kept for compatibility; used by parent with DateRange panel
  onPressSelectDates,
  initiallyOpenSection = 'projects',
  hideSearch = false,
}) => {
  const { t } = useTranslation('shared');
  const { projects, fetchFromCursor } = useListProjectsFromQuery({
    first: 25,
    sortBy: ProjectSortOption.NameAsc,
    archivesOnly: false,
  });

  const selectedDateRangeStr = useDateRangeString({
    selectedDueDateRange,
    useTranslationFormatter: true,
  });

  const [globalSearch, { data, loading: loadingSearch }] = useSearchLazyQuery();
  const searchData = (data?.search as Search<SearchableResult>[]) || [];

  const [search, setSearch] = useState<string>('');
  const [hoveredProjectId, setHoveredProjectId] = useState('');

  // useContacts() is a context read backed by a single query the app already
  // runs once at the root (ContactsProvider in CoreProviders) — calling it
  // here is free regardless of initialAssignees, so this can't (and doesn't
  // need to) skip the hook itself, only the derived work below (TA-10949,
  // PR #6012 review).
  const { contacts, loading: contactsLoading } = useContacts();
  const { me } = useMe();

  const listUsersToContacts = initialAssignees
    ? initialAssignees
    : convertUsersToContacts(
        !me || contacts.some((c) => c.id === me.id)
          ? contacts
          : [...contacts, me]
      );

  // Only the useContacts() fallback above has an in-flight loading state to
  // gate on — a caller-provided initialAssignees list is already resolved
  // data. In practice `me` (useMe()) usually resolves before or alongside
  // contacts, so contactsAndMe already has `me` in it by the time contacts
  // is still loading — this mostly only matters on a fully cold load
  // (neither me nor contacts has resolved yet, e.g. a hard reload), where
  // it renders a spinner instead of "No results found" (TA-10949, QA
  // round 3).
  const isAssigneeListLoading = !initialAssignees && contactsLoading;

  const [hoveredAssigneeId, setHoveredAssigneeId] = useState('');

  const [selectedPriorityList, setSelectedPriorityList] = useState<string[]>(
    initSelectedPriorities.map((t) => {
      switch (t) {
        case TaskPriority.Low:
          return 'Low';
        case TaskPriority.High:
          return 'High';
        default:
          return 'Medium';
      }
    })
  );

  const [hoveredPriority, setHoveredPriority] = useState('');

  // A selected assignee who has fallen out of listUsersToContacts (e.g. a
  // task's assignee who isn't one of the viewer's contacts) would otherwise
  // count toward the badge with no row left to untick it — keep them
  // visible even though they're not part of the "real" options pool
  // (TA-10949, QA round 3 follow-up).
  const selectedButMissingFromList = selectedAssignees.filter(
    (s) => !listUsersToContacts.some((u) => u.id === s.id)
  );

  // Previously-selected assignees float to the top each time this popup is
  // opened (TA-10949, product decision 2026-10-07 — matches mobile's
  // MyTasksFilterSheet/TasksListView via the same sortSelectedFirst util),
  // then ordering holds still while the user keeps toggling inside this one
  // open session — otherwise a row would jump position mid-click. The
  // selection is captured on mount rather than left out of the dependency
  // list below, so the memo's deps stay honest (same pattern as
  // FilterSheetContactList's selectedOnEntry).
  const selectedAssigneesOnEntry = useRef(selectedAssignees.map((s) => s.id));

  const sortedListUsersToContacts = sortSelectedFirst(
    [...listUsersToContacts, ...selectedButMissingFromList]
      .filter(
        (u) =>
          search.length === 0 ||
          u.displayName.toLowerCase().includes(search.toLowerCase().trim())
      )
      .sort((a, b) => (a.firstName ?? '')?.localeCompare(b.firstName ?? '')),
    selectedAssigneesOnEntry.current
  );

  // Toggle assignee selection and notify parent
  const onPressContact = (contact: ContactType) => {
    const isIn = selectedAssignees.some((c) => c.id === contact.id);
    const updatedSelected = isIn
      ? selectedAssignees.filter((c) => c.id !== contact.id)
      : [...selectedAssignees, contact];
    onSelectAssignee?.(updatedSelected);
  };

  const initialResults = {
    messages: [],
    tasks: [],
    documents: [],
    projects: [],
  };

  const results =
    searchData.reduce<SearchResultType>((previous, result) => {
      if (isProjectSearchResult(result)) {
        return { ...previous, projects: [...previous.projects, result] };
      }

      return previous;
    }, initialResults) || initialResults;

  const handleSearch = useCallback(
    (term: string) =>
      globalSearch({
        variables: {
          term,
          size: 20,
          includeMessages: false,
          includeDocuments: false,
          includeProjects: true,
          includeTasks: false,
        },
      }),
    [globalSearch]
  );

  const { isPending, isBelowThreshold, hasSearched } = useDebouncedSearch(
    search,
    handleSearch
  );
  // Gated on !isBelowThreshold (not just OR'd with loadingSearch) so a
  // still-in-flight request from before the term dropped below threshold
  // can't leak through and show a spinner instead of the browse list.
  const isSearchLoading =
    !isBelowThreshold && (loadingSearch || isPending || !hasSearched);

  // hasSearched gates the derived results so a stale response from an
  // earlier above-threshold search can't leak in once the term drops back
  // below threshold (TA-10168).
  const projectListData =
    !isBelowThreshold && hasSearched
      ? results.projects.map((r) => r.record)
      : [];

  // Toggle project selection and notify parent
  const onPressProject = (project: Project) => {
    const isIn = selectedProjects.some((c) => c.id === project.id);
    const updatedSelected = isIn
      ? selectedProjects.filter((c) => c.id !== project.id)
      : [...selectedProjects, project];
    onSelectProject?.(updatedSelected);
  };

  // =========================
  // Collapsible section state
  // =========================

  const isSearching = !isBelowThreshold;

  // 1) Compute lists and max heights first (they do not depend on open/close states)
  const displayProjectList = isBelowThreshold ? projects : projectListData;

  const MIN_VISIBLE_ROWS_WHEN_SEARCHING = 5;

  // When searching and we have many results, show at least 5 rows
  const visibleProjectRows =
    isSearching && displayProjectList.length >= MIN_VISIBLE_ROWS_WHEN_SEARCHING
      ? MIN_VISIBLE_ROWS_WHEN_SEARCHING
      : displayProjectList.length;

  const projectMaxHeight = Math.max(
    44,
    Math.min(visibleProjectRows * 36 + 8, PROJECT_SECTION_MAX_HEIGHT)
  );
  const assigneeMaxHeight = Math.max(
    44,
    Math.min(
      sortedListUsersToContacts.length * 36 + 8,
      ASSIGNEE_SECTION_MAX_HEIGHT
    )
  );

  // 2) Initial open/close states, based on initiallyOpenSection
  const [isProjectOpen, setIsProjectOpen] = useState(
    initiallyOpenSection === 'projects'
  );

  const [isAssigneeOpen, setIsAssigneeOpen] = useState(
    initiallyOpenSection === 'assignee'
  );

  const [isDueDateOpen, setIsDueDateOpen] = useState(
    initiallyOpenSection === 'duedate'
  );

  const [isPriorityOpen, setIsPriorityOpen] = useState(
    initiallyOpenSection === 'priority'
  );

  // 3) Animated values must respect the initial open/close state
  // Project
  const projectAnim = useRef(
    new Animated.Value(isProjectOpen ? projectMaxHeight : 0)
  ).current;
  const projectIconAnim = useRef(
    new Animated.Value(isProjectOpen ? 1 : 0)
  ).current;

  // Assignee
  const assigneeAnim = useRef(
    new Animated.Value(isAssigneeOpen ? assigneeMaxHeight : 0)
  ).current;
  const assigneeIconAnim = useRef(
    new Animated.Value(isAssigneeOpen ? 1 : 0)
  ).current;

  // Due Date (using a fixed max height here, consistent with your toggle logic)
  const dueDateAnim = useRef(
    new Animated.Value(isDueDateOpen ? DUE_DATE_SECTION_MAX_HEIGHT : 0)
  ).current;
  const dueDateIconAnim = useRef(
    new Animated.Value(isDueDateOpen ? 1 : 0)
  ).current;

  // Priority
  const priorityAnim = useRef(
    new Animated.Value(isPriorityOpen ? PRIORITY_SECTION_MAX_HEIGHT : 0)
  ).current;
  const priorityIconAnim = useRef(
    new Animated.Value(isPriorityOpen ? 1 : 0)
  ).current;

  // Remember previous "searching" state so we can react only when it changes
  const prevIsSearchingRef = useRef(isSearching);

  // =============================
  // Keep section heights in sync
  // when data length or search changes
  // =============================

  useEffect(() => {
    // When the project section is open and the list size changes,
    // animate the height to the new max value.
    if (isProjectOpen) {
      Animated.timing(projectAnim, {
        toValue: projectMaxHeight,
        duration: 200,
        useNativeDriver: false,
      }).start();
    }
  }, [projectMaxHeight, isProjectOpen, projectAnim]);

  useEffect(() => {
    // Same idea for assignee section: keep height in sync
    if (isAssigneeOpen) {
      Animated.timing(assigneeAnim, {
        toValue: assigneeMaxHeight,
        duration: 200,
        useNativeDriver: false,
      }).start();
    }
  }, [assigneeMaxHeight, isAssigneeOpen, assigneeAnim]);

  // =============================
  // Auto-open sections in search (only when search mode changes)
  // =============================

  useEffect(() => {
    const wasSearching = prevIsSearchingRef.current;
    const nowSearching = isSearching;

    const hasProjectResults = nowSearching && displayProjectList.length > 0;
    const hasAssigneeResults =
      nowSearching && sortedListUsersToContacts.length > 0;

    // Case 1: switch from NOT searching -> searching
    if (!wasSearching && nowSearching) {
      // Auto-open project section if it has results
      if (hasProjectResults && !isProjectOpen) {
        setIsProjectOpen(true);
        Animated.timing(projectAnim, {
          toValue: projectMaxHeight,
          duration: 200,
          useNativeDriver: false,
        }).start();
        Animated.timing(projectIconAnim, {
          toValue: 1,
          duration: 200,
          useNativeDriver: true,
        }).start();
      }

      // Auto-open assignee section if it has results
      if (hasAssigneeResults && !isAssigneeOpen) {
        setIsAssigneeOpen(true);
        Animated.timing(assigneeAnim, {
          toValue: assigneeMaxHeight,
          duration: 200,
          useNativeDriver: false,
        }).start();
        Animated.timing(assigneeIconAnim, {
          toValue: 1,
          duration: 200,
          useNativeDriver: true,
        }).start();
      }
    }

    // Case 2: switch from searching -> NOT searching (search cleared)
    if (wasSearching && !nowSearching) {
      // Reset to default state:
      // - Project open
      // - Other sections closed

      // Project open
      setIsProjectOpen(true);
      Animated.timing(projectAnim, {
        toValue: projectMaxHeight,
        duration: 200,
        useNativeDriver: false,
      }).start();
      Animated.timing(projectIconAnim, {
        toValue: 1,
        duration: 200,
        useNativeDriver: true,
      }).start();

      // Assignee closed
      setIsAssigneeOpen(false);
      Animated.timing(assigneeAnim, {
        toValue: 0,
        duration: 200,
        useNativeDriver: false,
      }).start();
      Animated.timing(assigneeIconAnim, {
        toValue: 0,
        duration: 200,
        useNativeDriver: true,
      }).start();

      // Due Date closed
      setIsDueDateOpen(false);
      Animated.timing(dueDateAnim, {
        toValue: 0,
        duration: 200,
        useNativeDriver: false,
      }).start();
      Animated.timing(dueDateIconAnim, {
        toValue: 0,
        duration: 200,
        useNativeDriver: true,
      }).start();

      // Priority closed
      setIsPriorityOpen(false);
      Animated.timing(priorityAnim, {
        toValue: 0,
        duration: 200,
        useNativeDriver: false,
      }).start();
      Animated.timing(priorityIconAnim, {
        toValue: 0,
        duration: 200,
        useNativeDriver: true,
      }).start();
    }

    // Update previous flag
    prevIsSearchingRef.current = nowSearching;
  }, [
    isSearching,
    displayProjectList.length,
    sortedListUsersToContacts.length,
    projectMaxHeight,
    assigneeMaxHeight,
    isProjectOpen,
    isAssigneeOpen,
    projectAnim,
    projectIconAnim,
    assigneeAnim,
    assigneeIconAnim,
    dueDateAnim,
    dueDateIconAnim,
    priorityAnim,
    priorityIconAnim,
  ]);

  // Helper to animate both height and icon for a section
  const animateSection = (
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>,
    heightAnim: Animated.Value,
    maxHeight: number,
    iconAnim: Animated.Value
  ) => {
    setIsOpen((prev) => {
      const next = !prev;
      const heightToValue = next ? maxHeight : 0;
      const iconToValue = next ? 1 : 0;

      Animated.timing(heightAnim, {
        toValue: heightToValue,
        duration: 200,
        useNativeDriver: false,
      }).start();

      Animated.timing(iconAnim, {
        toValue: iconToValue,
        duration: 200,
        useNativeDriver: true,
      }).start();

      return next;
    });
  };

  const toggleProjectSection = () => {
    // In non-search mode we keep "accordion" behavior:
    // opening one section closes the others.
    if (!isProjectOpen) {
      if (isAssigneeOpen && !isSearching) {
        toggleAssigneeSection();
      }
      if (isPriorityOpen) {
        togglePrioritySection();
      }
      if (isDueDateOpen) {
        toggleDueDateSection();
      }
    }

    animateSection(
      setIsProjectOpen,
      projectAnim,
      projectMaxHeight,
      projectIconAnim
    );
  };

  const toggleAssigneeSection = () => {
    if (!isAssigneeOpen) {
      if (!isSearching && isProjectOpen) {
        toggleProjectSection();
      }
      if (isPriorityOpen) {
        togglePrioritySection();
      }
      if (isDueDateOpen) {
        toggleDueDateSection();
      }
    }

    animateSection(
      setIsAssigneeOpen,
      assigneeAnim,
      assigneeMaxHeight,
      assigneeIconAnim
    );
  };

  const toggleDueDateSection = () => {
    if (!isDueDateOpen) {
      if (isProjectOpen) {
        toggleProjectSection();
      }
      if (isAssigneeOpen) {
        toggleAssigneeSection();
      }
      if (isPriorityOpen) {
        togglePrioritySection();
      }
    }

    animateSection(
      setIsDueDateOpen,
      dueDateAnim,
      DUE_DATE_SECTION_MAX_HEIGHT,
      dueDateIconAnim
    );
  };

  const togglePrioritySection = () => {
    if (!isPriorityOpen) {
      if (isProjectOpen) {
        toggleProjectSection();
      }
      if (isAssigneeOpen) {
        toggleAssigneeSection();
      }
      if (isDueDateOpen) {
        toggleDueDateSection();
      }
    }

    animateSection(
      setIsPriorityOpen,
      priorityAnim,
      PRIORITY_SECTION_MAX_HEIGHT,
      priorityIconAnim
    );
  };

  // ================================
  // Main filter content
  // ================================
  return (
    <ShadowBox
      flex={1}
      width={PANEL_WIDTH}
      alignItems='center'
      justifyContent='center'
      borderRadius='xs'
      borderWidth={1}
      borderColor='grey02'
      variant='base'
      backgroundColor='white'>
      <>
        {/* Search input */}
        {!hideSearch && (
          <Box
            marginHorizontal='m'
            marginTop='m'
            backgroundColor='grey02'
            style={{ width: 348 }}
            justifyContent='center'
            flexDirection='row'
            alignItems='flex-start'>
            <SearchInput
              value={search}
              height={40}
              onTextChanged={(value: string) => {
                setSearch(value);
              }}
              placeHolder={t('search')}
            />
          </Box>
        )}

        {/* ===================== Projects Section ===================== */}
        <Box style={{ width: PANEL_WIDTH }} mt='s'>
          <FilterSectionHeader
            showTopBorder={!hideSearch}
            title='Projects'
            badgeNumber={selectedProjects.length}
            onPress={toggleProjectSection}
            iconAnim={projectIconAnim}
            accessibilityLabel='Projects filter section'
          />

          {displayProjectList.length === 0 && isSearchLoading && (
            <Box my='xs'>
              <ActivityIndicatorLoading />
            </Box>
          )}

          {/* Animated container for project list */}
          <Animated.View
            style={{
              overflow: 'hidden',
              height: projectAnim,
            }}>
            <Box pb='xs' flex={1}>
              {!isSearchLoading && displayProjectList.length === 0 ? (
                <Box
                  px='m'
                  height={48}
                  alignItems='center'
                  justifyContent='center'>
                  <Text
                    variant='webBodySecondary'
                    color='grey04'
                    textAlign='center'>
                    No results found
                  </Text>
                </Box>
              ) : (
                <FlashList
                  // Only paginate the local project list when it's actually
                  // showing — above threshold this list displays remote
                  // search results instead, which don't paginate via
                  // fetchFromCursor (TA-10168).
                  onEndReached={isBelowThreshold ? fetchFromCursor : undefined}
                  showsVerticalScrollIndicator={false}
                  showsHorizontalScrollIndicator={false}
                  data={displayProjectList}
                  renderItem={({ item: project }) => {
                    const { id, name } = project;
                    return (
                      <Box
                        height={36}
                        borderLeftWidth={1}
                        borderLeftColor='grey02'
                        borderRightWidth={1}
                        borderRightColor='grey02'
                        justifyContent='center'
                        accessibilityLabel='Project Item'
                        style={{
                          backgroundColor:
                            hoveredProjectId !== '' &&
                            hoveredProjectId === project.id
                              ? theme.colors.grey01
                              : theme.colors.white,
                        }}
                        onMouseEnter={() => {
                          setHoveredProjectId(id);
                        }}
                        onMouseLeave={() => {
                          setHoveredProjectId('');
                        }}>
                        <TouchableOpacity
                          onPress={() => {
                            onPressProject(project);
                          }}
                          accessibilityLabel={name ? name : 'No Name'}>
                          <Box
                            flexDirection='row'
                            alignItems='center'
                            marginLeft='m'
                            justifyContent='space-between'>
                            <CheckBoxButton
                              checked={selectedProjects?.some(
                                (p) => p.id === id
                              )}
                            />

                            <Box ml='xs' mr='m' flex={1}>
                              <Text
                                variant='webBodySecondary'
                                color='textSecondary'
                                numberOfLines={1}>
                                <Highlighter
                                  highlightStyle={{
                                    backgroundColor: theme.colors.yellowBright,
                                  }}
                                  autoEscape
                                  searchWords={search?.split(' ') || ''}
                                  textToHighlight={name}
                                />
                              </Text>
                            </Box>
                          </Box>
                        </TouchableOpacity>
                      </Box>
                    );
                  }}
                  estimatedItemSize={ESTIMATED_ITEM_SIZE}
                />
              )}
            </Box>
          </Animated.View>
        </Box>

        {/* ===================== Assignee Section ===================== */}
        <Box style={{ width: PANEL_WIDTH }}>
          <FilterSectionHeader
            title='Assignee'
            badgeNumber={selectedAssignees.length}
            onPress={toggleAssigneeSection}
            iconAnim={assigneeIconAnim}
            accessibilityLabel='Assignee filter section'
          />

          {sortedListUsersToContacts.length === 0 && isAssigneeListLoading && (
            <Box my='xs'>
              <ActivityIndicatorLoading />
            </Box>
          )}

          <Animated.View
            style={{
              overflow: 'hidden',
              height: assigneeAnim,
            }}>
            <Box pb='xs' flex={1}>
              {!isAssigneeListLoading &&
              sortedListUsersToContacts.length === 0 ? (
                <Box
                  mx='m'
                  height={48}
                  alignItems='center'
                  justifyContent='center'>
                  <Text
                    variant='webBodySecondary'
                    color='grey04'
                    textAlign='center'>
                    No results found
                  </Text>
                </Box>
              ) : (
                <FlashList
                  showsVerticalScrollIndicator={false}
                  showsHorizontalScrollIndicator={false}
                  data={sortedListUsersToContacts}
                  renderItem={({ item: contact }) => {
                    const { id, displayName } = contact;
                    return (
                      <Box
                        height={36}
                        justifyContent='center'
                        accessibilityLabel='Assignee Item'
                        borderLeftWidth={1}
                        borderLeftColor='grey02'
                        borderRightWidth={1}
                        borderRightColor='grey02'
                        style={{
                          backgroundColor:
                            hoveredAssigneeId !== '' &&
                            hoveredAssigneeId === contact.id
                              ? theme.colors.grey01
                              : theme.colors.white,
                        }}
                        onMouseEnter={() => {
                          setHoveredAssigneeId(id);
                        }}
                        onMouseLeave={() => {
                          setHoveredAssigneeId('');
                        }}>
                        <TouchableOpacity
                          onPress={() => {
                            onPressContact(contact);
                          }}
                          accessibilityLabel={
                            displayName ? displayName : 'No Name'
                          }>
                          <Box
                            flexDirection='row'
                            alignItems='center'
                            justifyContent='space-between'>
                            <Box ml='m' justifyContent='center'>
                              <CheckBoxButton
                                checked={selectedAssignees?.some(
                                  (s) => s.id === contact.id
                                )}
                              />
                            </Box>
                            <Box mr='m' marginLeft='xs' flex={1}>
                              <Text
                                variant='webBodySecondary'
                                color={
                                  displayName ? 'textSecondary' : 'grey04'
                                }>
                                {displayName ? (
                                  <Highlighter
                                    highlightStyle={{
                                      backgroundColor:
                                        theme.colors.yellowBright,
                                    }}
                                    autoEscape
                                    searchWords={search?.split(' ') || ''}
                                    textToHighlight={displayName}
                                  />
                                ) : (
                                  'No name'
                                )}
                              </Text>
                            </Box>
                          </Box>
                        </TouchableOpacity>
                      </Box>
                    );
                  }}
                  estimatedItemSize={ESTIMATED_ITEM_SIZE}
                />
              )}
            </Box>
          </Animated.View>
        </Box>

        {/* ===================== Due Date Section ===================== */}
        <Box style={{ width: PANEL_WIDTH }}>
          <FilterSectionHeader
            title='Due Date'
            badgeNumber={
              selectedDueDateStrList.length +
              (selectedDueDateRange?.start || selectedDueDateRange?.end ? 1 : 0)
            }
            onPress={toggleDueDateSection}
            iconAnim={dueDateIconAnim}
            accessibilityLabel='Due date filter section'
          />

          <Animated.View
            style={{
              overflow: 'hidden',
              height: dueDateAnim,
            }}>
            <Box pb='xs' flex={1}>
              <FlashList
                showsVerticalScrollIndicator={false}
                showsHorizontalScrollIndicator={false}
                data={[
                  'Today',
                  'Tomorrow',
                  'Next 3 days',
                  selectedDateRangeStr ?? 'Select Dates',
                ]}
                renderItem={({ item, index }) => {
                  const isHovered = hoveredPriority === item;
                  const isSelectDates = index === 3;
                  const isChecked =
                    !isSelectDates &&
                    selectedDueDateStrList?.some((s) => s === item);

                  return (
                    <Box
                      height={36}
                      justifyContent='center'
                      accessibilityLabel='DueDate Item'
                      borderLeftWidth={1}
                      borderLeftColor='grey02'
                      borderRightWidth={1}
                      borderRightColor='grey02'
                      style={{
                        backgroundColor: isHovered
                          ? theme.colors.grey01
                          : theme.colors.white,
                      }}
                      onMouseEnter={() => {
                        setHoveredPriority(item);
                      }}
                      onMouseLeave={() => {
                        setHoveredPriority('');
                      }}>
                      <TouchableOpacity
                        onPress={() => {
                          if (isSelectDates) {
                            // Trigger parent to show date range panel
                            onPressSelectDates?.();
                          } else {
                            // const newV = selectedDueDateStrList.includes(item)
                            //   ? selectedDueDateStrList.filter((i) => i !== item)
                            //   : [...selectedDueDateStrList, item];
                            //
                            // onSelectedDueDateStrList?.(newV);
                            onSelectDueDateRange?.({
                              start: undefined,
                              end: undefined,
                            });
                            if (selectedDueDateStrList.includes(item)) {
                              onSelectedDueDateStrList?.([]);
                            } else {
                              onSelectedDueDateStrList?.([item]);
                            }
                          }
                        }}
                        accessibilityLabel={item}>
                        <Box
                          flexDirection='row'
                          alignItems='center'
                          justifyContent='space-between'>
                          <Box ml='m' justifyContent='center' marginRight='xs'>
                            {isSelectDates ? (
                              <Icon
                                name='Calendar'
                                color={
                                  isSelectDates && selectedDateRangeStr
                                    ? 'greenSecondary'
                                    : 'grey05'
                                }
                                variant='m'
                              />
                            ) : (
                              // <CheckBoxButton checked={isChecked} />
                              <RadioButton
                                iconVariant='m'
                                onPress={() => {
                                  // const newV = selectedDueDateStrList.includes(
                                  //   item
                                  // )
                                  //   ? selectedDueDateStrList.filter(
                                  //       (i) => i !== item
                                  //     )
                                  //   : [...selectedDueDateStrList, item];

                                  onSelectDueDateRange?.({
                                    start: undefined,
                                    end: undefined,
                                  });
                                  onSelectedDueDateStrList?.([item]);
                                }}
                                isSelected={isChecked}></RadioButton>
                            )}
                          </Box>

                          <Box mr='m' flex={1}>
                            <Text
                              variant='webBodySecondary'
                              color={
                                isSelectDates && selectedDateRangeStr
                                  ? 'greenSecondary'
                                  : 'textSecondary'
                              }>
                              {item}
                            </Text>
                          </Box>
                        </Box>
                      </TouchableOpacity>
                    </Box>
                  );
                }}
                estimatedItemSize={ESTIMATED_ITEM_SIZE}
              />
            </Box>
          </Animated.View>
        </Box>

        {/* ===================== Priority Section ===================== */}
        <Box style={{ width: PANEL_WIDTH }}>
          <FilterSectionHeader
            title='Priority'
            badgeNumber={selectedPriorityList.length}
            onPress={togglePrioritySection}
            iconAnim={priorityIconAnim}
            accessibilityLabel='Priority filter section'
          />

          <Animated.View
            style={{
              overflow: 'hidden',
              height: priorityAnim,
            }}>
            <Box pb='xs' flex={1}>
              <FlashList
                showsVerticalScrollIndicator={false}
                showsHorizontalScrollIndicator={false}
                data={['High', 'Medium', 'Low']}
                renderItem={({ item }) => {
                  const isHovered = hoveredPriority === item;
                  const isChecked = selectedPriorityList?.some(
                    (s) => s === item
                  );

                  return (
                    <Box
                      height={36}
                      justifyContent='center'
                      accessibilityLabel='Priority Item'
                      style={{
                        backgroundColor: isHovered
                          ? theme.colors.grey01
                          : theme.colors.white,
                      }}
                      borderLeftWidth={1}
                      borderLeftColor='grey02'
                      borderRightWidth={1}
                      borderRightColor='grey02'
                      onMouseEnter={() => {
                        setHoveredPriority(item);
                      }}
                      onMouseLeave={() => {
                        setHoveredPriority('');
                      }}>
                      <TouchableOpacity
                        onPress={() => {
                          let newV: string[] = [];
                          if (selectedPriorityList.includes(item)) {
                            newV = selectedPriorityList.filter(
                              (i) => i !== item
                            );
                          } else {
                            newV = [...selectedPriorityList, item];
                          }
                          setSelectedPriorityList(newV);
                          onSelectPriority?.(
                            newV.map((t) => {
                              switch (t) {
                                case 'Low':
                                  return TaskPriority.Low;
                                case 'High':
                                  return TaskPriority.High;
                                default:
                                  return TaskPriority.Medium;
                              }
                            })
                          );
                        }}
                        accessibilityLabel={item}>
                        <Box
                          flexDirection='row'
                          alignItems='center'
                          justifyContent='space-between'>
                          <Box ml='m' justifyContent='center'>
                            <CheckBoxButton checked={isChecked} />
                          </Box>

                          <Box mr='m' marginLeft='xs' flex={1}>
                            <Text
                              variant='webBodySecondary'
                              color='textSecondary'>
                              <Highlighter
                                highlightStyle={{
                                  backgroundColor: theme.colors.yellowBright,
                                }}
                                autoEscape
                                searchWords={search?.split(' ') || ''}
                                textToHighlight={item}
                              />
                            </Text>
                          </Box>
                        </Box>
                      </TouchableOpacity>
                    </Box>
                  );
                }}
                estimatedItemSize={ESTIMATED_ITEM_SIZE}
              />
            </Box>
          </Animated.View>
        </Box>

        {/* ===================== Footer / Clear All ===================== */}
        <Box
          width={PANEL_WIDTH}
          borderTopWidth={1}
          borderTopColor='grey02'
          height={48}
          alignItems='center'
          justifyContent='center'>
          <TouchableOpacity
            accessibilityLabel='Clear all filters'
            onPress={() => {
              setSelectedPriorityList([]);
              onSelectPriority?.([]);
              onSelectedDueDateStrList?.([]);
              onSelectDueDateRange?.({});
              onSelectProject?.([]);
              onSelectAssignee?.([]);
            }}>
            <Text
              flex={1}
              variant='webBodySecondary'
              color={
                selectedProjects.length > 0 ||
                selectedAssignees.length > 0 ||
                selectedPriorityList.length > 0 ||
                selectedDueDateStrList.length > 0
                  ? 'textPrimary'
                  : 'grey04'
              }>
              Clear All
            </Text>
          </TouchableOpacity>
        </Box>
      </>
    </ShadowBox>
  );
};
